import type { ReactNode, TdHTMLAttributes, ThHTMLAttributes, HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * The one table style for every page. Put it in a <Panel bodyClassName="px-0 pb-2">
 * so rows run edge to edge; wide tables scroll inside the panel, never the page.
 */
export function Table({ children, minWidth = 640, className }: { children: ReactNode; minWidth?: number; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full text-sm', className)} style={{ minWidth }}>{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="border-b border-border">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-border">{children}</tbody>;
}

export function Tr({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('transition-colors hover:bg-surface-raised/60', className)} {...props} />;
}

type Align = 'left' | 'right' | 'center';
const alignClass: Record<Align, string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

export function Th({ align = 'left', className, ...props }: ThHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return <th scope="col" className={cn('whitespace-nowrap px-5 py-2.5 text-xs font-medium text-muted-foreground', alignClass[align], className)} {...props} />;
}

/** `mono` for numbers, ids and codes (Geist Mono, tabular figures); `muted` for secondary columns. */
export function Td({ align = 'left', mono, muted, className, ...props }: TdHTMLAttributes<HTMLTableCellElement> & { align?: Align; mono?: boolean; muted?: boolean }) {
  return (
    <td
      className={cn(
        'px-5 py-3 align-middle',
        mono ? 'font-mono text-[13px] tabular-nums' : 'text-sm',
        muted ? 'text-muted-foreground' : 'text-foreground',
        alignClass[align],
        className,
      )}
      {...props}
    />
  );
}

/** Shaped placeholder rows while a table loads. */
export function TableSkeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2 px-5 pb-3', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton h-10" />)}
    </div>
  );
}
